import { useEffect, useState } from "react"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import UserService from "~services/UserService"
import UsersList from "~components/users/UsersList"
import PageContent from "~components/PageContent"
import NewUserModal from "~components/users/newUser"
import TableSearchField from "~components/TableSearchField"
import { TableTopGroup } from "~styled/table"
import { ButtonPrimarySoftLarge } from "~styled/button"
import { RBAC_RULES } from "~rbac/constants/rbac-rules"
import IsAuthorized from "~rbac/isAuthorized"
import { UsersListShape, UserShape } from "~shapes/user"
import { TablePaginatorShape, TableOptionsShape } from "~shapes/api-table"
import PageHeader from "~components/PageHeader"
import { Card } from "~components/Card"
import { toast } from "react-toastify"

const Users = () => {
  // Set component state
  const [user, setUser] = useState<UserShape>(null!)
  const [users, setUsers] = useState<UserShape[]>([])
  const [paginator, setPaginator] = useState<TablePaginatorShape>(null!)
  const [isGettingUser, setIsGettingUser] = useState<boolean>(false)
  const [isModalOpened, setIsModalOpened] = useState<boolean>(false)
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false)

  const tableOptions = { sort: "createdAt,desc", size: 15 }

  // Function to update a user in the local state
  const updateUserInList = (updatedUser: UserShape) => {
    setUsers(prevUsers =>
      prevUsers.map(user => (user.id === updatedUser.id ? updatedUser : user)),
    )
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  // Method to fetch users and save them in the state
  const fetchUsers = async (options?: Partial<TableOptionsShape>) => {
    setIsTableLoading(true)

    try {
      const res: UsersListShape = await UserService.getUsers({
        ...tableOptions,
        ...options,
      })

      setPaginator(res.page)
      setUsers(res._embedded.users)
    } catch (err) {
      setPaginator(null!)
      setUsers([])
    }

    setIsTableLoading(false)
  }

  // Method to handle search
  const handleSearch = (query: { email?: string }) => {
    fetchUsers({ email: query.email })
  }

  // Method to handle the onPageChange method from the paginator
  const handlePageChange = (id: number) => {
    fetchUsers({ page: id })
  }

  // Method to handle the Modal Open
  const handleModalClose = (shouldRefreshList?: boolean) => {
    if (shouldRefreshList) fetchUsers({ page: paginator.number })
    setUser(null!)
    setIsModalOpened(false)
  }

  // Method to handle the User Edit click
  const handleUserEditClick = async (id: string) => {
    setIsGettingUser(true)
    setIsModalOpened(true)

    try {
      const response = await UserService.getUserById(id)
      setUser(response)
    } catch (err) {
      setUser(null!)
    }

    setIsGettingUser(false)
  }

  const handleUserDeleteClick = (user: UserShape) => {
    if (user.id) {
      if (
        confirm(
          `Deletion is a permanent action. User data will be destroyed. Confirm deletion for user ${user.email}?`,
        )
      ) {
        UserService.DELETEUser(user.id)
          .then(async () => {
            toast.success(`User deleted with success.`)
            await fetchUsers()
          })
          .catch(() => {
            toast.error(`There was an error, try again.`)
          })
      }
    }
  }

  return (
    <PageContent>
      <PageHeader title={"Users"} />
      <Card>
        <TableTopGroup style={{ marginBottom: 20 }}>
          <TableSearchField
            searchBy={["email"]}
            placeholder="Search by email..."
            callback={handleSearch}
            debounceMs={400}
          />

          <IsAuthorized action={RBAC_RULES.CREATE_USER_ACCOUNT}>
            <ButtonPrimarySoftLarge onClick={() => setIsModalOpened(true)}>
              <AccountCircleIcon />
              Create a New User
            </ButtonPrimarySoftLarge>
          </IsAuthorized>
        </TableTopGroup>

        <UsersList
          users={users}
          isLoading={isTableLoading}
          onClickUserEdit={handleUserEditClick}
          onRefetchUsers={fetchUsers}
          onPageChange={handlePageChange}
          onClickUserDelete={handleUserDeleteClick}
        />
      </Card>

      {/* UploadProofOfOwnershipModal/Update User Modal */}
      {isModalOpened && (
        <NewUserModal
          user={user}
          isGettingUser={isGettingUser}
          onRequestClose={handleModalClose}
          onUserUpdated={updateUserInList}
        />
      )}
    </PageContent>
  )
}

export default Users
